import React, { useEffect, useState } from 'react';
import { Mail, Loader2, Trash2, Reply, CheckCircle2, Archive, X, Search, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  adminListContactMessages,
  adminGetContactMessage,
  adminMarkContactMessage,
  adminDeleteContactMessage
} from '../../services/api';

const STATUS_STYLE = {
  new: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-600',
};

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ContactMessagesTab = () => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread | new | in_progress | resolved | archived
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter === 'unread') params.unreadOnly = true;
      else if (filter !== 'all') params.status = filter;
      const r = await adminListContactMessages(params);
      setMessages(r.data.items || []);
      setTotal(r.data.total || 0);
      setUnread(r.data.unread || 0);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (id) => {
    try {
      const r = await adminGetContactMessage(id);
      setSelected(r.data);
      setMessages(prev => prev.map(m => m.contact_id === id ? { ...m, read: true } : m));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const setStatus = async (id, status) => {
    try { await adminMarkContactMessage(id, { status }); await load(); if (selected?.contact_id === id) setSelected(prev => ({ ...prev, status })); } catch { /* ignore */ }
  };
  const markReplied = async (id) => {
    try { await adminMarkContactMessage(id, { replied: true, status: 'in_progress' }); await load(); if (selected?.contact_id === id) setSelected(prev => ({ ...prev, replied: true, status: 'in_progress' })); } catch { /* ignore */ }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this contact message permanently?')) return;
    try { await adminDeleteContactMessage(id); setSelected(null); await load(); } catch { /* ignore */ }
  };

  const filtered = messages.filter((m) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q) || (m.subject || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="contact-messages-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Outfit' }}>Contact Inbox</h3>
          <p className="text-sm text-gray-500">Every message submitted via the public Contact Us page.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-600"><b>{unread}</b> unread · <b>{total}</b> total</div>
        </div>
      </div>

      {/* Filters + search */}
      <div className="bg-white rounded-2xl shadow-sm p-3 flex flex-wrap items-center gap-2">
        {['all', 'unread', 'new', 'in_progress', 'resolved', 'archived'].map(k => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === k ? 'border-[#009E60] bg-[#009E60]/5 text-[#007A4B]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            data-testid={`contact-filter-${k}`}
          >
            {k === 'all' ? 'All' : k === 'unread' ? 'Unread' : k.replace('_', ' ')}
          </button>
        ))}
        <div className="flex-1 min-w-[180px]" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, subject" className="pl-9 h-10 rounded-xl w-64" data-testid="contact-search" />
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#009E60] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12" data-testid="contact-empty">
            <Mail className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <div className="text-gray-500">No contact messages found.</div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map(m => (
              <button
                key={m.contact_id}
                onClick={() => openDetail(m.contact_id)}
                className={`w-full text-left p-4 flex items-start gap-3 hover:bg-gray-50 ${!m.read ? 'bg-[#009E60]/5' : ''}`}
                data-testid={`contact-row-${m.contact_id}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${!m.read ? 'bg-[#009E60] text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <Mail className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 truncate">{m.subject || '(no subject)'}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[m.status] || 'bg-gray-100'}`}>{(m.status || 'new').toUpperCase().replace('_', ' ')}</span>
                    {!m.read && <span className="w-2 h-2 rounded-full bg-[#009E60]" />}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate"><b>{m.name}</b> · {m.email}</div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{m.message}</p>
                </div>
                <div className="text-xs text-gray-400 flex-shrink-0">{fmt(m.created_at)}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-3" onClick={() => setSelected(null)} data-testid="contact-detail">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 truncate">{selected.subject}</h3>
              <button onClick={() => setSelected(null)} className="p-1 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm">
                <div><span className="text-gray-500">From: </span><b className="text-gray-900">{selected.name}</b> &lt;<a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`} className="text-[#009E60] underline" data-testid="contact-mailto">{selected.email}</a>&gt;</div>
                <div className="text-xs text-gray-500 mt-1">Received {fmt(selected.created_at)}{selected.user_id ? ` · logged-in user ${selected.user_id}` : ''}</div>
                {selected.email_notified_at && <div className="text-xs text-green-600 mt-1">Admin email notification sent</div>}
              </div>
              <div className="bg-gray-50 rounded-2xl p-3 text-sm text-gray-800 whitespace-pre-wrap" data-testid="contact-detail-body">{selected.message}</div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => { window.location.href = `mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`; markReplied(selected.contact_id); }}
                  className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
                  data-testid="contact-reply-btn"
                >
                  <Reply className="w-4 h-4 mr-2" /> Reply via email
                </Button>
                <Button variant="outline" onClick={() => setStatus(selected.contact_id, 'in_progress')} className="rounded-full" data-testid="contact-in-progress-btn">
                  <ExternalLink className="w-4 h-4 mr-2" /> In progress
                </Button>
                <Button variant="outline" onClick={() => setStatus(selected.contact_id, 'resolved')} className="rounded-full" data-testid="contact-resolve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Resolved
                </Button>
                <Button variant="outline" onClick={() => setStatus(selected.contact_id, 'archived')} className="rounded-full" data-testid="contact-archive-btn">
                  <Archive className="w-4 h-4 mr-2" /> Archive
                </Button>
                <Button variant="ghost" onClick={() => remove(selected.contact_id)} className="text-red-500 hover:bg-red-50 rounded-full ml-auto" data-testid="contact-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMessagesTab;
